import intl from 'react-intl-universal'
import type { ComponentType, SVGProps } from 'react'
import { LOCALE } from '@foxrenderfarm/internals'
import { LightDropDownSection, LightNavDropDownWrapper } from '../../atoms'
import PricingMenuDiwaliOfferIcon from '../../svg/pricing-menu-diwali-offer.svg'
import PricingMenuCostEstimatorIcon from '../../svg/pricing-menu-cost-estimator.svg'
import PricingMenuNewUserBonusIcon from '../../svg/pricing-menu-new-user-bonus.svg'
import PricingMenuPricingIcon from '../../svg/pricing-menu-pricing.svg'
import { LightNavDropDownPanel, MainLink, Trigger, getLocalAppOrigin, withLocale } from './common'

interface PricingLightDropDownProps {
  currentLocale: LOCALE
  currentPathname: string
  origin: string
}

const PricingMenuIcon = ({ icon }: { icon: unknown }) => {
  const iconProps = {
    'aria-hidden': true,
    width: 20,
    height: 20,
    style: { flexShrink: 0, marginRight: 8 },
  } as const

  const normalizedIcon =
    icon && typeof icon === 'object' && 'default' in icon
      ? (icon as { default?: unknown }).default
      : icon

  if (typeof normalizedIcon === 'string') {
    return <img src={normalizedIcon} alt='' {...iconProps} />
  }

  if (typeof normalizedIcon !== 'function') return null

  const SvgIcon = normalizedIcon as ComponentType<SVGProps<SVGSVGElement>>
  return <SvgIcon focusable='false' {...iconProps} />
}

export const PricingLightDropDown = ({ currentLocale, origin }: PricingLightDropDownProps) => {
  const useGatsbyLink = process.env.GATSBY_PACKAGE_NAME === 'main'
  const mainOrigin = getLocalAppOrigin(origin, 8084)

  const items = [
    {
      key: 'pricing',
      labelKey: 'index.header.pricing.overview',
      to: '/pricing.html',
      icon: PricingMenuPricingIcon,
    },
    {
      key: 'new-user-bonus',
      labelKey: 'index.header.pricing.bonus',
      to: '/new-user-bonus.html',
      icon: PricingMenuNewUserBonusIcon,
    },
    {
      key: 'cost-estimator',
      labelKey: 'index.header.pricing.estimator',
      to: '/pricing.html#costestimator',
      icon: PricingMenuCostEstimatorIcon,
    },
    {
      key: 'diwali-offer',
      label: 'India Diwali Offer',
      to: '/diwali-special-offer.html',
      icon: PricingMenuDiwaliOfferIcon,
    },
  ]
  const visibleItems = items.filter((item) => item.key !== 'diwali-offer')

  return (
    <LightNavDropDownWrapper $isActive={false} $panelMinWidth={260}>
      <Trigger>{intl.get('index.header.pricing')}</Trigger>
      <LightNavDropDownPanel panelTitle={intl.get('index.header.pricing')}>
        <LightDropDownSection>
          {visibleItems.map((item) => {
            const isAnchorLink = item.to.includes('#')
            const Icon = item.icon
            const localizedTo = withLocale(item.to, currentLocale)

            return (
              <MainLink
                key={item.key}
                to={localizedTo}
                href={`${mainOrigin}${localizedTo}`}
                useGatsbyLink={useGatsbyLink && !isAnchorLink}
                locale={currentLocale}
              >
                <PricingMenuIcon icon={Icon} />
                {'label' in item ? item.label : intl.get(item.labelKey)}
              </MainLink>
            )
          })}
        </LightDropDownSection>
      </LightNavDropDownPanel>
    </LightNavDropDownWrapper>
  )
}
